import { api } from '../../app/api/baseApi';
import { FIXTURE_API_URL } from '../../constants';
import { CACHE_TIMEOUT_DURATION } from '../../constants/cacheTimeout';
import { PAGINATION_PAYLOAD } from '../../constants/paginationQuery';
import type { UpdateLoadDetailsRequest } from '../../types/deviceLoads';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

export type HeartbeatEmailRecord = {
    id: string;
    organizationId: string;
    organizationName: string;
    deviceName: string;
    deviceSerialNumber: string;
    deviceModel: string;
    type: string;
    receivedTimestamp: number;
    heartbeatDeltaMinutes: number;
    result: string;
};

export type HeartbeatEmailsRequest = {
    headers?: { 'x-realm': string; organizationId: string };
    filters: {
        emailTypes: ['Heartbeat'];
        start: number;
        end: number;
        serialNumber: string;
    };
    page?: number;
    size?: number;
};

export type HeartbeatEmailsResponse = {
    success: boolean;
    data: { total: number; records: HeartbeatEmailRecord[] };
};

export type HeartbeatEmailDetailRequest = {
    emailId: string;
    headers?: { 'x-realm'?: string; organizationId?: string };
};

export type HeartbeatEmailDetailResponse = {
    success: boolean;
    data?: Record<string, unknown>;
    requestId?: string;
};

export type HeartbeatEmailsDownloadRequest = {
    headers?: { 'x-realm'?: string; organizationId?: string };
    columns?: string[];
    filters?: {
        emailTypes?: Array<'Activation' | 'Heartbeat' | 'Alarm'>;
        end?: number;
        model?: string;
        organizationName?: string;
        result?: string;
        serialNumber?: string;
        start?: number;
    };
    page?: number;
    searchKey?: string;
    size?: number;
    timezone?: string;
    type?: 'CSV';
};

export type HeartbeatEmailsDownloadResponse = {
    success: boolean;
    data?: string;
    requestId?: string;
};

export type MigratableDevicesFilterRequest = {
    filters: {
        days?: number;
        installedOnFrom?: number;
        installedOnTo?: number;
        deviceName?: string;
        deviceStatus?: string[];
        groups?: string[];
        id?: string[];
        model?: string;
        modelCategory?: string[];
        serialNumber?: string[];
    };
    page: number;
    searchKey: string;
    size: number;
    sort: {
        key:
            | 'installedOn'
            | 'serialNumber'
            | 'name'
            | 'model'
            | 'deviceStatus'
            | 'id'
            | 'type'
            | 'count.alarmCount'
            | 'count.upcomingCount'
            | 'alarmCount'
            | 'upcomingCount';
        sortType: 'ASC' | 'DESC';
    };
};

const buildRealmHeaders = (realmName?: string, organizationId?: string): { headers?: Record<string, string> } => {
    const headers: Record<string, string> = {};
    if (realmName) headers['x-realm'] = realmName;
    if (organizationId) headers.organizationId = organizationId;
    return Object.keys(headers).length ? { headers } : {};
};

type GetDeviceByIdQueryArg = string | undefined | { deviceId: string; organizationId?: string; realmPrefix?: string };

export type MigratableDeviceRecord = {
    connectionStatus?: string;
    count?: {
        alarmCount?: number;
        offlineCount?: number;
        upcomingCount?: number;
        warningCount?: number;
    };
    deviceModel?: string;
    deviceStatus?: string;
    deviceTypeId?: string;
    deviceTypeName?: string;
    groupId?: string;
    groupName?: string;
    id?: string;
    installedOn?: string | number;
    model?: string;
    modelCategory?: string;
    name?: string;
    organizationId?: string;
    serialNumber?: string;
};

type MigratableDevicesFilterResponse = {
    data?: {
        records?: MigratableDeviceRecord[];
        total?: number;
    };
    requestId?: string;
    success?: boolean;
};

/** A single channel entry returned by `GET dm/v1/devices/{deviceId}/scorecard`. */
export type DeviceScorecardChannel = {
    name: string;
    icon?: {
        name?: string;
        URL?: string;
        isExternal?: boolean;
        family?: string;
        iconLibrary?: { name?: string };
        web?: { name?: string; family?: string };
        mobile?: { name?: string; family?: string };
    };
    unit?: string;
    tagId?: string;
    value?: string;
    key?: string;
};

/** The device's health grade, returned by `GET dm/v1/devices/{deviceId}/scorecard` when a score
 *  is available (e.g. email ingestion / PredictPulse applies to the device); `null` otherwise. */
export type DeviceScorecardHealthScore = {
    grade: string;
    maxScore: number;
    scoreName: string;
    scoreColor: string;
    currentScore: number;
};

export type DeviceScorecardData = {
    healthScore: DeviceScorecardHealthScore | null;
    channels: DeviceScorecardChannel[];
    isEmpty?: boolean;
};

export type DeviceScorecardResponse = {
    success: boolean;
    data?: DeviceScorecardData;
    requestId?: string;
};

export type DeviceScorecardRequest = {
    deviceId: string | undefined;
    realmPrefix?: string;
    organizationId?: string;
};

export type DeviceTypeLoadKeyMapperResponse = {
    success: boolean;
    data?: {
        deviceTypeIdLoadKeyMapper?: Record<string, string>;
    };
    requestId?: string;
};

export type DeviceTypeLoadKeyMapperRequest = {
    realmPrefix?: string;
    organizationId?: string;
};

/** Backend limit for `DELETE dm/v1/devices/bulk`. */
export const BULK_DELETE_DEVICES_MAX_IDS = 25;

export type BulkDeleteDevicesRequest = {
    deviceIds: string[];
};

export type BulkDeleteDeviceResult = {
    deviceId: string;
    status: 'SUCCESS' | 'FAILED';
    reason: string | null;
};

export type BulkDeleteDevicesResponse = {
    success: boolean;
    data?: {
        results?: BulkDeleteDeviceResult[];
    };
};

export const MIGRATE_DEVICES_MAX_IDS = 25;

export type MigrateDevicesRequest = {
    destinationOrgId: string;
    destinationRealmId: string;
    deviceIds: string[];
};

export type MigrateDeviceResult = {
    deviceId: string;
    status: 'SUCCESS' | 'FAILED';
    reason: string | null;
};

export type MigrateDevicesResponse = {
    success: boolean;
    requestId?: string;
    data?: { results?: MigrateDeviceResult[] };
};

export const deviceApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getHeartbeatEmails: builder.query<HeartbeatEmailsResponse, HeartbeatEmailsRequest>({
            query: ({ headers, ...body }) => ({
                url: 'dm/v1/communications/emails/filter',
                method: 'POST',
                body,
                ...(headers && { headers }),
            }),
            providesTags: ['DeviceList'],
            keepUnusedDataFor: 0,
        }),
        getHeartbeatEmailById: builder.query<HeartbeatEmailDetailResponse, HeartbeatEmailDetailRequest>({
            query: ({ emailId, headers }) => ({
                url: `dm/v1/communications/emails/${encodeURIComponent(emailId)}`,
                method: 'GET',
                ...(headers && { headers }),
            }),
            keepUnusedDataFor: 0,
        }),
        getHeartbeatEmailDownload: builder.query<HeartbeatEmailsDownloadResponse, HeartbeatEmailDetailRequest>({
            query: ({ emailId, headers }) => ({
                url: `dm/v1/communications/emails/${encodeURIComponent(emailId)}/download`,
                method: 'GET',
                ...(headers && { headers }),
            }),
            keepUnusedDataFor: 0,
            extraOptions: 'no-toast',
        }),
        downloadHeartbeatEmails: builder.mutation<HeartbeatEmailsDownloadResponse, HeartbeatEmailsDownloadRequest>({
            query: ({ headers, ...body }) => ({
                url: 'dm/v1/communications/emails/download',
                method: 'POST',
                body,
                ...(headers && { headers }),
            }),
            extraOptions: 'no-toast',
        }),
        getConsumptionBreakdownData: builder.query({
            query: (body) => ({
                url: 'ems/v1/consumption-breakdown',
                method: 'POST',
                body,
            }),
        }),
        getLoadTrendsData: builder.query({
            query: (body) => ({
                url: 'ems/v1/load-trend',
                method: 'POST',
                body,
            }),
        }),
        getMetricsWidgetData: builder.query({
            query: (body) => ({
                url: 'ems/v1/efficiency',
                method: 'POST',
                body,
            }),
        }),
        getAssetsListData: builder.query({
            query: (widgetTypeId) => ({
                url: 'ems/v1/asset',
                method: 'GET',
                params: { widgetTypeId },
            }),
            keepUnusedDataFor: CACHE_TIMEOUT_DURATION,
        }),
        getEnergyFLowAssetsData: builder.query({
            query: (body) => ({
                url: `ems/v1/energy/flow`,
                method: 'POST',
                body,
            }),
            keepUnusedDataFor: CACHE_TIMEOUT_DURATION,
        }),
        getConsumptionBreakdownAssets: builder.query({
            query: () => ({
                url: 'ems/v1/asset-list/cbd',
                method: 'GET',
            }),
        }),
        getAggregatedBarChartData: builder.query({
            query: (body) => ({
                url: 'ems/v1/chart/bar',
                method: 'POST',
                body,
            }),
        }),
        getAggregatedLineChartData: builder.query({
            query: (body) => ({
                url: 'ems/v1/chart/line',
                method: 'POST',
                body,
            }),
        }),
        getWeatherData: builder.query({
            query: (body) => ({
                url: 'ems/v1/weather-forecast',
                method: 'POST',
                body,
            }),
            keepUnusedDataFor: CACHE_TIMEOUT_DURATION,
        }),
        getAllSchedulesData: builder.query({
            query: (body) => ({
                url: 'automation/v1/schedule/summary',
                method: 'POST',
                body,
            }),
        }),
        getAssetSchedule: builder.query({
            query: (body) => ({
                url: 'automation/v1/schedule/list/ungrouped',
                method: 'POST',
                body,
            }),
        }),
        getAssetTypeSchedule: builder.query({
            query: (body) => ({
                url: 'automation/v1/schedule/list/grouped',
                method: 'POST',
                body,
            }),
        }),
        getAllAssetTypesDropdown: builder.query({
            query: (body) => ({
                url: 'automation/v1/schedule/allAssetTypes',
                method: 'POST',
                body,
            }),
        }),
        getAllActionTypesDropdown: builder.query({
            query: (body) => ({
                url: 'automation/v1/schedule/allActionTypes',
                method: 'POST',
                body,
            }),
        }),
        getCalendarSchedule: builder.query({
            query: (body) => ({
                url: 'automation/v1/schedule/list/week-view',
                method: 'POST',
                body,
            }),
        }),
        getAllSchedulesList: builder.query({
            query: (body) => ({
                url: 'automation/v1/schedule/filters',
                method: 'POST',
                body,
            }),
        }),
        getAllDevices: builder.query({
            query: (body) => {
                const bodyClone = JSON.parse(JSON.stringify(body));
                if (bodyClone.headers) {
                    delete bodyClone.headers;
                }
                return {
                    url: 'dm/v1/devices/filter',
                    method: 'POST',
                    body: bodyClone,
                    ...(body.headers && { headers: body.headers }),
                };
            },
            keepUnusedDataFor: 0.000001,
            providesTags: ['DeviceList'],
        }),
        getMigratableDevices: builder.query<MigratableDevicesFilterResponse, MigratableDevicesFilterRequest>({
            query: (body) => ({
                url: 'dm/v1/devices/migratable/filter',
                method: 'POST',
                body: {
                    ...body,
                    sort: {
                        ...body.sort,
                        key:
                            body.sort.key === 'count.alarmCount'
                                ? 'alarmCount'
                                : body.sort.key === 'count.upcomingCount'
                                ? 'upcomingCount'
                                : body.sort.key,
                    },
                },
            }),
            extraOptions: { excludeRealmHeader: true, excludeOrgId: true },
            keepUnusedDataFor: 0.000001,
            providesTags: ['DeviceList'],
        }),
        getAllMigratableDevices: builder.query<
            MigratableDevicesFilterResponse,
            Pick<MigratableDevicesFilterRequest, 'filters' | 'searchKey'>
        >({
            queryFn: (body, queryApi, _extraOptions, baseQuery) => {
                const records: MigratableDeviceRecord[] = [];
                const loadPage = async (
                    page: number
                ): Promise<{ data: MigratableDevicesFilterResponse } | { error: FetchBaseQueryError }> => {
                    if (queryApi.signal.aborted) {
                        return { error: { status: 'CUSTOM_ERROR', error: 'Aborted' } };
                    }
                    const result = await baseQuery({
                        url: 'dm/v1/devices/migratable/filter',
                        method: 'POST',
                        body: { ...body, page, size: 25, sort: { key: 'installedOn', sortType: 'DESC' } },
                    });
                    if (result.error) return { error: result.error };
                    const response = result.data as MigratableDevicesFilterResponse;
                    const pageRecords = response?.data?.records;
                    const total = response?.data?.total;
                    if (
                        response?.success === false ||
                        !Array.isArray(pageRecords) ||
                        typeof total !== 'number' ||
                        !Number.isSafeInteger(total) ||
                        total < 0 ||
                        records.length + pageRecords.length > total
                    ) {
                        return { error: { status: 'CUSTOM_ERROR', error: 'Incomplete device list' } };
                    }
                    records.push(...pageRecords);
                    if (queryApi.signal.aborted) {
                        return { error: { status: 'CUSTOM_ERROR', error: 'Aborted' } };
                    }
                    if (records.length < total) {
                        if (!pageRecords.length) {
                            return { error: { status: 'CUSTOM_ERROR', error: 'Incomplete device list' } };
                        }
                        return loadPage(page + 1);
                    }
                    return { data: { data: { records, total: records.length } } };
                };
                return loadPage(0);
            },
            extraOptions: { excludeRealmHeader: true, excludeOrgId: true },
            keepUnusedDataFor: 0.000001,
            providesTags: ['DeviceList'],
        }),
        getAllSubscriptionDevices: builder.query({
            query: ({ body, params }) => {
                const bodyClone = JSON.parse(JSON.stringify(body));
                if (bodyClone.headers) {
                    delete bodyClone.headers;
                }
                return {
                    url: 'dm/v1/devices/filter',
                    method: 'POST',
                    ...(params && { params }),
                    body: bodyClone,
                    ...(body.headers && { headers: body.headers }),
                };
            },
            keepUnusedDataFor: 0.000001,
            providesTags: ['DeviceList'],
        }),
        getDeviceList: builder.mutation({
            query: ({ body, headers }: any) => ({
                url: 'dm/v1/devices/filter',
                method: 'POST',
                body,
                ...(headers && { headers }),
            }),
        }),
        getAllDeviceStatus: builder.query({
            query: (headers?: { 'x-realm'?: string; organizationId?: string }) => ({
                url: 'dm/v1/devices/status',
                ...(headers && { headers }),
            }),
        }),
        getDeviceTable: builder.query({
            query: (tableType) => ({
                url: 'iam/v1/users/me/ui/config',
                params: { tableType },
            }),
            providesTags: ['GET-TABLE'],
        }),
        getChannelDetailsWithCategory: builder.query({
            query: ({ deviceId, params }) => ({
                url: `dm/v1/devices/${deviceId}/channels/category`,
                params,
            }),
            keepUnusedDataFor: 0,
        }),
        updateDeviceTable: builder.mutation({
            query: ({ body, tableType }: any) => ({
                url: 'iam/v1/users/me/ui/config',
                method: 'PUT',
                params: { tableType: tableType },
                body,
            }),
            invalidatesTags: (res, err): any => (!err ? ['GET-TABLE'] : []),
        }),
        getMobileDevices: builder.query<any, any>({
            query: (body) => {
                const payloadClone = JSON.parse(JSON.stringify(body));
                delete payloadClone.headers;
                if (payloadClone.pullRefresh) {
                    delete payloadClone.pullRefresh;
                }
                return {
                    url: 'dm/v1/devices/filter',
                    method: 'POST',
                    body: payloadClone,
                    ...(body.headers && { headers: body.headers }),
                };
            },
            ...PAGINATION_PAYLOAD,
            providesTags: ['DeviceList'],
        }),
        getDeviceByProfile: builder.query({
            query: (sourceId) => ({
                url: `dm/v1/device/types/device/${sourceId}`,
            }),
        }),

        //will be removed once master data api is used for retrieving device types

        getAllDeviceTypes: builder.query({
            query: (args) => ({
                url: 'dm/v1/device/types/filter',
                method: 'POST',
                ...(args?.body && { body: args?.body }),
                ...(args?.parent && { params: { parent: args?.parent } }),
            }),
        }),

        getAllChannels: builder.query({
            query: ({ sourceId, params, body, realmPrefix }: any) => ({
                url: `dm/v1/device/types/${sourceId}/channels`,
                params,
                method: 'GET',
                ...(body && { body }),
                ...(realmPrefix && { headers: { 'x-realm': realmPrefix } }),
            }),
            transformResponse: (response: any, _meta: any, args: any) => {
                const responseClone = JSON.parse(JSON.stringify(response));
                if (args?.number) {
                    responseClone.data = responseClone.data.filter((record: any) => record.dataType === 'Number');
                } else if (args?.isLoadsConfigurationRequired) {
                    responseClone.data = responseClone?.data?.map((record: any) => ({
                        loadId: record?.channelId ?? record?.channelKey,
                        loadName: record?.displayName ?? record?.name,
                        icon: record?.icon,
                    }));
                }
                return responseClone;
            },
        }),

        getSearchedGatewayOrGroup: builder.query({
            query: ({ search, headers }: any) => ({
                url: `ems/v1/assets/hierarchy`,
                ...(search && { params: { search } }),
                method: 'GET',
                ...(headers && { headers }),
            }),
        }),

        getChannelUnits: builder.query({
            query: (body: any) => ({
                url: `dm/v1/device/types/units`,
                body,
                method: 'POST',
            }),
        }),
        getDeviceAlertCount: builder.query({
            query: (body: any) => ({
                url: `alarm/v1/count`,
                body,
                method: 'POST',
            }),
        }),
        getDeviceById: builder.query<any, GetDeviceByIdQueryArg>({
            queryFn: (arg: GetDeviceByIdQueryArg, _queryApi, _extraOptions, baseQuery) => {
                const queryArgs = typeof arg === 'string' ? { deviceId: arg } : arg;
                if (!queryArgs?.deviceId) {
                    return { error: { status: 'CUSTOM_ERROR', error: 'deviceId is required' } };
                }

                const { deviceId, organizationId, realmPrefix } = queryArgs;
                const headers = {
                    ...(organizationId && { organizationId }),
                    ...(realmPrefix && { 'x-realm': realmPrefix }),
                };
                return baseQuery({
                    url: `dm/v1/devices/${deviceId}`,
                    ...(Object.keys(headers).length && { headers }),
                });
            },
            providesTags: ['DeviceById'],
        }),
        getDeviceScorecard: builder.query<DeviceScorecardResponse, DeviceScorecardRequest>({
            query: ({ deviceId, realmPrefix, organizationId }) => ({
                url: `dm/v1/devices/${deviceId}/scorecard`,
                method: 'GET',
                ...((realmPrefix || organizationId) && {
                    headers: {
                        ...(realmPrefix && { 'x-realm': realmPrefix }),
                        ...(organizationId && { organizationId }),
                    },
                }),
            }),
            // Backend 400s when a device has no health score (e.g. email ingestion not
            // applicable) — expected per the hide-card business rule, not a user-facing error.
            extraOptions: 'no-toast',
            keepUnusedDataFor: 0,
        }),
        getDeviceTypeLoadKeyMapper: builder.query<DeviceTypeLoadKeyMapperResponse, DeviceTypeLoadKeyMapperRequest>({
            query: ({ realmPrefix, organizationId }) => ({
                url: 'dm/v1/devices/load-keys',
                method: 'GET',
                ...((realmPrefix || organizationId) && {
                    headers: {
                        ...(realmPrefix && { 'x-realm': realmPrefix }),
                        ...(organizationId && { organizationId }),
                    },
                }),
            }),
            keepUnusedDataFor: 60,
        }),
        getDeviceTimeline: builder.query({
            query: ({ body, params }: any) => ({ url: `alarm/v1/timeline/filter`, method: 'POST', body, params }),
        }),

        getMobileDeviceTimeline: builder.query({
            query: (body) => {
                const payloadClone = JSON.parse(JSON.stringify(body));
                if (payloadClone.pullRefresh) {
                    delete payloadClone.pullRefresh;
                }
                return {
                    url: `alarm/v1/timeline/filter`,
                    method: 'POST',
                    body: payloadClone,
                };
            },
            ...PAGINATION_PAYLOAD,
        }),

        getChannelDetails: builder.mutation({
            query: (body) => ({
                url: 'dm/v1/devices/details',
                method: 'POST',
                body,
            }),
        }),
        getDeviceTrends: builder.mutation({
            query: ({ headers, ...body }) => ({
                url: 'dm/v1/devices/trends',
                method: 'POST',
                body,
                ...(headers && { headers }),
            }),
        }),
        getLoadGroupTrends: builder.mutation({
            query: (body) => ({
                url: 'dm/v1/devices/trends',
                method: 'POST',
                body,
            }),
        }),
        getUIMetadata: builder.mutation({
            query: (body) => ({
                url: 'dm/v1/device/types/meta/data',
                method: 'POST',
                body,
            }),
        }),
        getMasterData: builder.mutation({
            query: (body) => ({
                url: 'dm/v1/device/types/master/data',
                method: 'POST',
                body,
            }),
        }),
        getModelAssembly: builder.mutation<any, { id: string }>({
            query: ({ id }) => ({
                url: `ontology/v1/models/modelassembly`,
                method: 'GET',
                params: { id },
            }),
        }),
        getDeviceModelCountStatus: builder.query({
            query: (body) => ({
                url: 'dm/v1/device/types/master/data',
                method: 'POST',
                body,
            }),
        }),
        createDevice: builder.mutation({
            query: (body) => ({
                url: `/dm/v1/devices`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error): any =>
                !error ? ['DeviceList', 'GroupsHierarchy', 'Orgs', 'allSubscriptionsV2', 'allSubscriptions'] : [],
            extraOptions: 'no-toast',
        }),
        editDevice: builder.mutation<object, any>({
            query: (body) => ({
                url: `/dm/v1/devices/${body.id}`,
                method: 'PATCH',
                body: body.payload,
            }),
            invalidatesTags: (result, error): any => (!error ? ['GroupsHierarchy', 'DeviceList'] : []),
        }),
        createDeviceMobile: builder.mutation({
            query: (body) => ({
                url: `/dm/v1/devices`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error): any =>
                !error ? ['DeviceList', 'allSubscriptionsV2', 'allSubscriptions'] : [],
            extraOptions: 'no-toast',
        }),
        getChannelThresholds: builder.query({
            query: ({ channelId, deviceId }: any) => ({
                url: `dm/v1/device-threshold/${deviceId}/${channelId}`,
            }),
            keepUnusedDataFor: 0.0000001,
        }),
        getSearchedDeviceOrGroup: builder.query({
            query: (body): any => ({
                body,
                method: 'POST',
                url: `iam/v1/groups/hierarchy/search`,
            }),
        }),
        generateDeviceName: builder.mutation({
            query: (body) => ({
                url: 'dm/v1/devices/valid/name',
                method: 'POST',
                body,
            }),
        }),
        getThreshold: builder.query({
            query: ({ channelKey, deviceId }: any) => ({
                url: `dm/v1/device-threshold/${deviceId}/${channelKey}`,
            }),
            keepUnusedDataFor: 0.000001,
            providesTags: ['Threshold'],
        }),
        updateThreshold: builder.mutation({
            query: ({ channelKey, deviceId, body }) => ({
                url: `dm/v1/device-threshold/${deviceId}/${channelKey}`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (result, error): any => (!error ? ['Threshold'] : []),
        }),
        getSettings: builder.query({
            query: ({ deviceId, debouncedValue }) => ({
                url: `dm/v1/devices/${deviceId}/device-settings`,
                params: {
                    ...(debouncedValue && { searchKey: 'displayName', searchValue: debouncedValue }),
                },
            }),
            providesTags: ['device-settings'],
        }),
        updateSettings: builder.mutation({
            query: ({ body, deviceId }) => ({
                url: `dm/v1/device-settings/${deviceId}/command`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error): any => (!error ? ['device-settings'] : []),
        }),
        sendDeviceCommand: builder.mutation({
            query: ({ body, deviceId }) => ({
                url: `dm/v1/devices/${deviceId}/command`,
                method: 'POST',
                body,
            }),
        }),

        getDeviceSettings: builder.mutation({
            query: (body) => ({
                url: `dm/v1/device-settings`,
                method: 'POST',
                body,
            }),
        }),
        updateDevice: builder.mutation({
            query: ({ deviceId, body }) => ({
                url: `dm/v1/devices/${deviceId}`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (result, error): any => (!error ? ['Properties', 'DeviceList', 'DeviceById'] : []),
        }),
        deleteDevice: builder.mutation({
            query: ({ headers, ...body }: any) => ({
                url: 'dm/v1/devices',
                body,
                method: 'DELETE',
                ...(headers && { headers }),
            }),
            invalidatesTags: (result, error): any =>
                !error ? ['DeviceList', 'allSubscriptionsV2', 'allSubscriptions'] : [],
        }),
        getMeshStatusId: builder.query({
            query: ({ meshId }) => ({
                url: `mic/v1/provisioning/status/${meshId}`,
                method: 'GET',
            }),
            extraOptions: { skipAuthModalOn401: true },
            providesTags: ['LoadDetails'],
        }),

        bulkDeleteDevices: builder.mutation<BulkDeleteDevicesResponse, BulkDeleteDevicesRequest>({
            query: (body) => ({
                url: 'dm/v1/devices/bulk',
                method: 'DELETE',
                body,
            }),
            invalidatesTags: (_result, error) =>
                !error ? ['DeviceList', 'allSubscriptionsV2', 'allSubscriptions'] : [],
        }),
        migrateDevices: builder.mutation<MigrateDevicesResponse, MigrateDevicesRequest>({
            query: (body) => ({
                url: 'dm/v1/devices/migrate',
                method: 'POST',
                body,
            }),
            extraOptions: { excludeOrgId: true },
            invalidatesTags: (_result, error) =>
                !error ? ['DeviceList', 'GroupsHierarchy', 'allSubscriptionsV2', 'allSubscriptions'] : [],
        }),
        getAllLoads: builder.query({
            query: ({ deviceId, body, isRampShedRequired }) => {
                return {
                    url: `dm/v1/device-loads/${deviceId}`,
                    method: 'POST',
                    body: body ?? { page: 0, size: 10 },
                    params: isRampShedRequired !== null ? { isRampShedRequired } : undefined,
                };
            },
            providesTags: ['AllLoads'],
        }),
        getMultiDeviceLoads: builder.query({
            query: (body) => ({
                url: `dm/v1/device-loads/all/loads`,
                method: 'POST',
                body: body,
            }),
            providesTags: ['MultiLoads'],
        }),
        getAllVirtualLoads: builder.query({
            query: (args) => {
                const { realmName, organizationId, ...body } = args ?? {};
                return {
                    url: 'dm/v1/connected-loads/filter',
                    method: 'POST',
                    body,
                    ...buildRealmHeaders(realmName, organizationId),
                };
            },

            providesTags: ['Virtual_Loads'],
        }),
        getMobileVirtualLoads: builder.query({
            query: (body) => {
                const payloadClone = JSON.parse(JSON.stringify(body));
                if (payloadClone.pullRefresh) {
                    delete payloadClone.pullRefresh;
                }
                return {
                    url: 'dm/v1/connected-loads/filter',
                    method: 'POST',
                    body: payloadClone,
                };
            },
            ...PAGINATION_PAYLOAD,
            providesTags: ['Virtual_Loads'],
        }),
        getAllVirtualLoadTypes: builder.query<any, { realmName?: string; organizationId?: string } | void>({
            query: (args) => ({
                url: 'dm/v1/connected-loads/types',
                ...buildRealmHeaders(args?.realmName, args?.organizationId),
            }),
        }),

        createVirtualLoad: builder.mutation({
            query: (body) => ({
                url: `dm/v1/connected-loads`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error): any => (!error ? ['Virtual_Loads'] : []),
        }),
        getDetailsOfLoadById: builder.query({
            query: ({ id, loadId }) => ({
                url: `dm/v1/device-loads/${id}/${loadId}`,
                method: 'GET',
            }),
            providesTags: ['LoadDetails'],
        }),
        getDeviceProperties: builder.query({
            // `realmPrefix` overrides the redux-selected realm's `x-realm` header for callers
            // (e.g. Service Admin) browsing a device that belongs to a different realm.
            query: ({ deviceId, debouncedValue, organizationId, realmPrefix }) => {
                const headers = {
                    ...(organizationId && { organizationId }),
                    ...(realmPrefix && { 'x-realm': realmPrefix }),
                };
                return {
                    url: `dm/v1/devices/${deviceId}/properties`,
                    ...(Object.keys(headers).length && { headers }),
                    params: {
                        ...(debouncedValue && { searchKey: 'displayName', searchValue: debouncedValue }),
                    },
                };
            },
            providesTags: ['Properties'],
        }),
        updateStatusOfLoad: builder.mutation({
            query: ({ id, loadId, command }) => {
                return {
                    url: `dm/v1/device-loads/${id}/${loadId}/command`,
                    method: 'PATCH',
                    body: {
                        command,
                    },
                };
            },
            invalidatesTags: (result, error): any =>
                !error ? ['AllLoads', 'LoadDetails', 'MultiLoads', 'getLoadGroupsById', 'LoadGroupsById'] : [],
        }),
        getAvailableCommands: builder.query({
            query: (body) => {
                return {
                    url: `dm/v1/devices/available/controls`,
                    method: 'POST',
                    body: {
                        controlRequestSet: body,
                    },
                };
            },
        }),
        updateLoadDetails: builder.mutation({
            query: ({ deviceId, body }: UpdateLoadDetailsRequest) => ({
                url: `dm/v1/device-loads/${deviceId}`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (result, error): any =>
                !error ? ['LoadDetails', 'AllLoads', 'MultiLoads', 'DeviceById'] : [],
        }),
        getLoadList: builder.query({
            query: (body: any) => ({
                url: '/dm/v1/connected-loads/filter',
                method: 'POST',
                body,
            }),
            ...PAGINATION_PAYLOAD,
        }),
        exportTrends: builder.mutation({
            query: ({ body, params }: any) => ({
                url: 'dm/v1/devices/export',
                method: 'POST',
                body,
                params,
            }),
        }),
        deviceMap: builder.mutation({
            query: (body) => ({
                url: 'dm/v1/devices/map',
                method: 'POST',
                body,
            }),
        }),
        getVirtualLoadById: builder.query({
            query: (connectedLoadId) => ({
                url: `dm/v1/connected-loads/${connectedLoadId}`,
                method: 'GET',
            }),
            providesTags: ['VirtualLoadDetails'],
        }),
        deleteVirtualLoadById: builder.mutation({
            query: (connectedLoadId) => ({
                url: `dm/v1/connected-loads/${connectedLoadId}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error): any => (!error ? ['Virtual_Loads'] : []),
        }),
        updateVirtualLoadById: builder.mutation({
            query: ({ connectedLoadId, body }: any) => ({
                url: `dm/v1/connected-loads/${connectedLoadId}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error): any => (!error ? ['Virtual_Loads', 'VirtualLoadDetails'] : []),
        }),
        updateDeviceConfig: builder.mutation({
            query: ({ deviceId, body }) => ({
                url: `dm/v1/devices/${deviceId}`,
                method: 'PUT',
                body: { payload: body.payload },
            }),
            invalidatesTags: (result, error): any => (!error ? ['Properties', 'DeviceList'] : []),
        }),
        getDeviceStatus: builder.query({
            query: () => ({
                url: `dm/v1/devices/status`,
                method: 'GET',
            }),
        }),
        getModelsData: builder.query({
            query: (category) => ({
                url: 'ontology/api/v1/models/category',
                method: 'GET',
                params: { category },
            }),
        }),
        getComponentFilter: builder.query({
            query: (body) => ({
                url: '/ds/v1/demo/filter',
                method: 'POST',
                body,
            }),
        }),
        createSchedule: builder.mutation({
            query: (body) => ({
                url: `automation/v1/schedule`,
                method: 'POST',
                body,
            }),
        }),
        getScheduleDetails: builder.query({
            query: ({ id, time }) => ({
                url: `automation/v1/schedule/pop-up/${id}?date=${time}`,
                method: 'GET',
            }),
        }),
        downloadChartData: builder.mutation({
            query: (body) => ({
                url: 'ems/v1/chart/download',
                method: 'POST',
                body,
            }),
        }),
        // Validate Secure Scanning
        validateSecureScanning: builder.mutation({
            query: (body) => ({
                url: `mic/v1/light/trusted/validate`,
                method: 'POST',
                body,
            }),
        }),
        updateDeviceConfigMIC: builder.mutation({
            query: ({ deviceId, body }) => ({
                url: `dm/v1/devices/${deviceId}`,
                method: 'PUT',
                body: body, //{ payload: body },
            }),
            invalidatesTags: (result, error): any => (!error ? ['Properties', 'DeviceList'] : []),
        }),
        updateDaylightSaving: builder.mutation<
            object,
            { meshId: string; zones: Array<{ zoneId: string; devices: Array<{ deviceId: string; version: string }> }> }
        >({
            query: (body) => ({
                url: `mic/v1/daylightsaving/devices/status/update`,
                method: 'PUT',
                body,
            }),
        }),
        updateDeviceNameMIC: builder.mutation({
            query: ({ deviceId, body }) => ({
                url: `dm/v1/devices/${deviceId}`,
                method: 'PUT',
                body: body,
            }),
            invalidatesTags: (result, error): any => (!error ? ['Properties', 'DeviceList', 'DeviceById'] : []),
        }),
        deleteDeviceMIC: builder.mutation({
            query: (body) => ({
                url: 'dm/v1/devices',
                body: body,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error): any => (!error ? ['DeviceList', 'LoadDetails'] : []),
        }),
        getDeviceType: builder.query({
            query: () => ({
                url: `${FIXTURE_API_URL}deviceTypes`,
                method: 'GET',
            }),
        }),
        getDeviceLicensesDevices: builder.query({
            query: (body) => {
                const { headers, ...restBody } = body ?? {};
                delete restBody.pullRefresh;
                return {
                    url: 'dm/v1/devices/filter?isDeleted=true',
                    method: 'POST',
                    body: restBody,
                    ...(headers && { headers }),
                };
            },
            ...PAGINATION_PAYLOAD,
            providesTags: ['DeviceList'],
        }),
        getDeviceLicensesDevicesPaginated: builder.query({
            query: (body) => {
                const { headers, ...restBody } = body ?? {};
                return {
                    url: 'dm/v1/devices/filter?isDeleted=true',
                    method: 'POST',
                    body: restBody,
                    ...(headers && { headers }),
                };
            },
            keepUnusedDataFor: 0.00001,
            providesTags: ['DeviceList'],
        }),
        getExportStatus: builder.query({
            query: () => ({
                url: 'dm/v1/devices/export-jobs',
                method: 'GET',
            }),
            keepUnusedDataFor: 0,
            providesTags: ['ExportStatus'],
        }),
        exportData: builder.mutation({
            query: (body: { fromTime: number; toTime: number; deviceIds: string[]; groupIds: string[] }) => ({
                url: 'dm/v1/devices/export-jobs',
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error): any => (!error ? ['ExportStatus'] : []),
        }),
        getExportDownloadUrl: builder.query<
            {
                success: boolean;
                data: {
                    organizationId: string;
                    downloadUrl: string;
                    expiresAt: string;
                    startedAt: string;
                    fileSizeBytes: number;
                };
                requestId: string;
            },
            void
        >({
            query: () => ({
                url: 'dm/v1/devices/export-jobs/download',
                method: 'GET',
            }),
            keepUnusedDataFor: 0,
        }),
    }),
    overrideExisting: true,
});

export const {
    useGetConsumptionBreakdownDataQuery,
    useGetLoadTrendsDataQuery,
    useLazyGetEnergyFLowAssetsDataQuery,
    useLazyGetMetricsWidgetDataQuery,
    useGetAssetsListDataQuery,
    useGetConsumptionBreakdownAssetsQuery,
    useGetMetricsWidgetDataQuery,
    useGetEnergyFLowAssetsDataQuery,
    useLazyGetAggregatedBarChartDataQuery,
    useLazyGetAggregatedLineChartDataQuery,
    useLazyGetWeatherDataQuery,
    useGetModelsDataQuery,
    useLazyGetAllSchedulesDataQuery,
    useLazyGetAssetScheduleQuery,
    useLazyGetAssetTypeScheduleQuery,
    useLazyGetAllAssetTypesDropdownQuery,
    useLazyGetAllActionTypesDropdownQuery,
    useLazyGetCalendarScheduleQuery,
    useLazyGetAllSchedulesListQuery,
    useGetAllSubscriptionDevicesQuery,
    useGetAllDevicesQuery,
    useGetMigratableDevicesQuery,
    useGetAllMigratableDevicesQuery,
    useBulkDeleteDevicesMutation,
    useMigrateDevicesMutation,
    useDeviceMapMutation,
    useGetDeviceSettingsMutation,
    useLazyGetConsumptionBreakdownDataQuery,
    useGetAllLoadsQuery,
    useLazyGetAllLoadsQuery,
    useGetAllDeviceStatusQuery,
    useUpdateSettingsMutation,
    useGetSearchedDeviceOrGroupQuery,
    useGetChannelUnitsQuery,
    useGetMultiDeviceLoadsQuery,
    useGetSettingsQuery,
    useGetMobileDevicesQuery,
    useGetAllDeviceTypesQuery,
    useGetAllVirtualLoadsQuery,
    useGetAllVirtualLoadTypesQuery,
    useGetAllChannelsQuery,
    useGetSearchedGatewayOrGroupQuery,
    useLazyGetAllChannelsQuery,
    useLazyGetLoadTrendsDataQuery,
    useUpdateDeviceTableMutation,
    useGetDeviceTableQuery,
    useGetDeviceByProfileQuery,
    useSendDeviceCommandMutation,
    useGetDevicePropertiesQuery,
    useGetChannelThresholdsQuery,
    useGetChannelDetailsMutation,
    useGetDeviceTrendsMutation,
    useGetUIMetadataMutation,
    useGetDeviceAlertCountQuery,
    useGetMasterDataMutation,
    useGetModelAssemblyMutation,
    useGetChannelDetailsWithCategoryQuery,
    useCreateDeviceMutation,
    useEditDeviceMutation,
    useCreateDeviceMobileMutation,
    useGetDeviceByIdQuery,
    useGetDeviceScorecardQuery,
    useGetDeviceTypeLoadKeyMapperQuery,
    useGetDeviceTimelineQuery,
    useGetMobileDeviceTimelineQuery,
    useGenerateDeviceNameMutation,
    useGetThresholdQuery,
    useLazyGetThresholdQuery,
    useUpdateThresholdMutation,
    useUpdateDeviceMutation,
    useGetMobileVirtualLoadsQuery,
    useDeleteDeviceMutation,
    useGetDetailsOfLoadByIdQuery,
    useGetMeshStatusIdQuery,
    useLazyGetMeshStatusIdQuery,
    useUpdateStatusOfLoadMutation,
    useUpdateLoadDetailsMutation,
    useLazyGetDeviceByIdQuery,
    useGetLoadListQuery,
    useExportTrendsMutation,
    useCreateVirtualLoadMutation,
    useGetVirtualLoadByIdQuery,
    useDeleteVirtualLoadByIdMutation,
    useUpdateVirtualLoadByIdMutation,
    useUpdateDeviceConfigMutation,
    useGetDeviceListMutation,
    useGetDeviceStatusQuery,
    useCreateScheduleMutation,
    useGetScheduleDetailsQuery,
    useDownloadChartDataMutation,
    useValidateSecureScanningMutation,
    useUpdateDaylightSavingMutation,
    useUpdateDeviceConfigMICMutation,
    useUpdateDeviceNameMICMutation,
    useDeleteDeviceMICMutation,
    useGetDeviceTypeQuery,
    useGetLoadGroupTrendsMutation,
    useGetDeviceLicensesDevicesQuery,
    useGetDeviceLicensesDevicesPaginatedQuery,
    useGetDeviceModelCountStatusQuery,
    useGetExportStatusQuery,
    useExportDataMutation,
    useLazyGetExportDownloadUrlQuery,
    useGetHeartbeatEmailsQuery,
    useGetHeartbeatEmailByIdQuery,
    useLazyGetHeartbeatEmailDownloadQuery,
    useDownloadHeartbeatEmailsMutation,
} = deviceApi;
